import type {
  QueryDocumentSnapshot,
  DocumentData,
  WhereFilterOp,
} from "firebase/firestore";
import {
  collection,
  doc,
  getFirestore,
  limit,
  onSnapshot,
  orderBy,
  or,
  query,
  startAfter,
  where,
  startAt,
  getDocs,
} from "firebase/firestore";
import { defineStore, storeToRefs } from "pinia";
import { ref, computed, watch } from "vue";
import { caseConverter } from "~~/converters/case-converter";
import type { ICase } from "~~/types/case";
import { PermissionType, EAccountType } from "~~/types/user";
import type { CaseSearchFilter } from "~~/types/case-search";
import useUserStore from "./use-user-store";

const PHONE_SEARCH_FIELDS = ["userData.customer.contactNum"];
// CASE_READ_SELF queries add an or(...) of up to 6 conditions; Firestore allows at most 30
// disjunctions, so their phone `in` list is capped at 30 / 6 values.
const PHONE_SEARCH_MAX_VALUES_WITH_OR = 5;

const useCaseStorePrivate = defineStore("case-private", () => {
  return {
    caseDocs: ref<QueryDocumentSnapshot<unknown, DocumentData>[]>([]),
    selectedCaseRegNum: ref<string>(""),
    hasMore: ref(true),
  };
});

const useCaseStore = defineStore("case", () => {
  const { caseDocs, selectedCaseRegNum, hasMore } = storeToRefs(
    useCaseStorePrivate(),
  );
  const db = getFirestore();
  const cases = ref<ICase[]>([]);
  const selectedCase = ref<ICase>();
  const pageCursors = ref<{ [pageNum: number]: any }>({});
  const pageSize = ref(15);
  const searchFilters = ref<CaseSearchFilter[]>([]);
  // phone filter field -> Firestore condition resolved once per search (reused across pages)
  const resolvedPhoneConditions = ref<Record<string, PhoneSearchCondition>>({});
  let phoneResolveRequestId = 0;
  const currentPage = ref<number>(0);
  let unsubscribeCases = () => {};
  let unsubscribeCase = () => {};

  const userStore = useUserStore();
  const { currentUser } = storeToRefs(userStore);

  // Helper function to check if user has access to a specific case
  const checkUserCaseAccess = (user: any, caseData: ICase): boolean => {
    if (!user || !caseData) return false;

    const userId = user.id;
    const companyId = user.company?.id;

    // Check if user is directly involved
    if (userId) {
      if (caseData.userData?.provider?.id === userId) return true;
      if (caseData.userData?.previousProvider?.id === userId) return true;
      if (caseData.userData?.client?.id === userId) return true;
    }

    // Check if user's company matches
    if (companyId) {
      if (caseData.userData?.client?.company?.id === companyId) return true;
      if (caseData.userData?.provider?.company?.id === companyId) return true;
      if (caseData.userData?.previousProvider?.company?.id === companyId)
        return true;
    }

    return false;
  };

  // Helper function to filter case data based on user role
  const filterCaseByRole = (caseData: ICase): ICase => {
    const userAccountType = userStore.currentUser?.accountType;

    if (!caseData) return caseData;

    const filteredCase = { ...caseData };

    if (userAccountType === EAccountType.CLIENT) {
      // For CLIENT role: hide provider, provider pricing, customer pricing, timestamps (except createdAt), all remarks
      if (filteredCase.userData) {
        filteredCase.userData = { ...filteredCase.userData };
        // Hide provider
        delete (filteredCase.userData as any).provider;
        delete (filteredCase.userData as any).previousProvider;
      }

      if (filteredCase.payment) {
        filteredCase.payment = { ...filteredCase.payment };
        // Hide provider pricing
        if (filteredCase.payment.provider) {
          filteredCase.payment.provider = { pay: 0, receive: 0 };
        }
        // Hide customer pricing
        if (filteredCase.payment.customer) {
          filteredCase.payment.customer = { payProvider: 0, payRsa: 0 };
        }
      }

      if (filteredCase.statusTimestamp) {
        // Only keep createdAt timestamp
        filteredCase.statusTimestamp = {
          createdAt: filteredCase.statusTimestamp.createdAt,
        } as ICase["statusTimestamp"];
      }

      // Hide all remarks
      filteredCase.internalRemarks = [];
      filteredCase.externalRemarks = [];
    } else if (userAccountType === EAccountType.PROVIDER) {
      // For PROVIDER role: hide client, client pricing, customer pay rsa pricing, internal remarks, policy
      if (filteredCase.userData) {
        filteredCase.userData = { ...filteredCase.userData };
        // Hide client
        delete (filteredCase.userData as any).client;
      }

      if (filteredCase.payment) {
        filteredCase.payment = { ...filteredCase.payment };
        // Hide client pricing
        if (filteredCase.payment.client) {
          filteredCase.payment.client = { pay: 0, receive: 0 };
        }
        // Hide customer pay rsa pricing (keep payProvider)
        if (filteredCase.payment.customer) {
          filteredCase.payment.customer = {
            ...filteredCase.payment.customer,
            payRsa: 0,
          };
        }
      }

      // Hide internal remarks
      filteredCase.internalRemarks = [];

      // Hide policy
      delete (filteredCase as any).policyType;
    }

    return filteredCase;
  };

  const generateCaseRegNumInfo = async (
    maxRetries = 5,
  ): Promise<{ regNum: string; clientRegNum: string }> => {
    const getFormattedDate = (date: Date) => {
      const year = date.getFullYear().toString();
      const month = (1 + date.getMonth()).toString().padStart(2, "0");
      return month + year.slice(-2);
    };

    const formattedDate = getFormattedDate(new Date());

    for (let attempt = 0; attempt < maxRetries; attempt++) {
      const randNumString = Math.floor(Math.random() * (99999 - 1))
        .toString()
        .padStart(5, "0");

      const [existingRegNum, existingClientRegNum] = await Promise.all([
        getDocs(
          query(
            collection(db, `cases`),
            where(`regNum`, `==`, `CS${formattedDate}${randNumString}`),
            limit(1),
          ),
        ),
        getDocs(
          query(
            collection(db, `cases`),
            where(`clientRegNum`, `==`, `CL${formattedDate}${randNumString}`),
            limit(1),
          ),
        ),
      ]);

      if (existingRegNum?.empty && existingClientRegNum?.empty) {
        return {
          regNum: `CS${formattedDate}${randNumString}`,
          clientRegNum: `CL${formattedDate}${randNumString}`,
        };
      }
    }

    return { regNum: "", clientRegNum: "" };
  };

  const fetchCases = async (page: number = 0) => {
    return new Promise((resolve, reject) => {
      try {
        const userStore = useUserStore();
        const userPermissions = userStore.currentUser?.permissions || [];
        const currentUser = userStore.currentUser;

        // Check if user has permission to read cases
        const hasCaseReadOther = userPermissions.includes(
          PermissionType.CASE_READ_OTHER,
        );
        const hasCaseReadSelf = userPermissions.includes(
          PermissionType.CASE_READ_SELF,
        );

        if (!hasCaseReadOther && !hasCaseReadSelf) {
          // Clear existing data and resolve without fetching
          caseDocs.value = [];
          cases.value = [];
          hasMore.value = false;
          resolve(null);
          return;
        }

        const searchQueries = searchFilters.value.flatMap((filter) => {
          if (filter.operator === "startsWith") {
            return [
              where(filter.field, ">=", filter.value),
              where(filter.field, "<=", filter.value + "\uf8ff"),
            ];
          }
          if (
            filter.operator === "==" &&
            PHONE_SEARCH_FIELDS.includes(filter.field)
          ) {
            const { operator, value } =
              resolvedPhoneConditions.value[filter.field] ??
              phoneSearchCondition(filter.value);
            return [where(filter.field, operator, value)];
          }
          return [
            where(filter.field, filter.operator as WhereFilterOp, filter.value),
          ];
        });

        // A range (startsWith) filter field must lead the orderBy list, otherwise
        // Firestore rejects the query (missing-index / invalid-query error)
        const rangeField = searchFilters.value.find(
          (filter) => filter.operator === "startsWith",
        )?.field;
        const orderBys = rangeField
          ? [
              orderBy(rangeField, "asc"),
              orderBy("statusTimestamp.createdAt", "desc"),
            ]
          : [orderBy("statusTimestamp.createdAt", "desc")];

        // If user only has CASE_READ_SELF, use OR query to find cases where user has relationship
        if (!hasCaseReadOther && hasCaseReadSelf && currentUser) {
          unsubscribeCases();
          currentPage.value = page;

          const userId = currentUser.id;
          const companyId = currentUser.company?.id;

          // Build OR conditions for user relationships
          const orConditions: any[] = [];

          if (userId) {
            // User is directly involved as provider, previousProvider, or client
            orConditions.push(where("userData.provider.id", "==", userId));
            orConditions.push(
              where("userData.previousProvider.id", "==", userId),
            );
            orConditions.push(where("userData.client.id", "==", userId));
          }

          if (companyId) {
            // User's company matches client, provider, or previousProvider company
            orConditions.push(
              where("userData.client.company.id", "==", companyId),
            );
            orConditions.push(
              where("userData.provider.company.id", "==", companyId),
            );
            orConditions.push(
              where("userData.previousProvider.company.id", "==", companyId),
            );
          }

          if (orConditions.length === 0) {
            // No valid conditions, return empty
            caseDocs.value = [];
            cases.value = [];
            hasMore.value = false;
            resolve(null);
            return;
          }

          const constraints: any[] = [
            ...searchQueries,
            or(...orConditions),
            ...orderBys,
            limit(pageSize.value + 5),
          ];

          // Add pagination cursor if not first page
          if (page > 0 && pageCursors.value[page]) {
            constraints.push(startAfter(pageCursors.value[page]));
          }

          const q = query(
            collection(db, "cases"),
            ...constraints,
          ).withConverter(caseConverter);

          unsubscribeCases = onSnapshot(
            q,
            (querySnapshot) => {
              const docs = querySnapshot.docs.slice(0, pageSize.value); // Take only pageSize items

              caseDocs.value = docs;
              cases.value = docs.map((doc) => ({
                id: doc.id,
                ...doc.data(),
              })) as ICase[];

              // Save cursor for next page if we have results that is more than pageSize, meaning next page still has data
              if (
                querySnapshot.docs.length > pageSize.value &&
                docs[docs.length - 1]
              ) {
                pageCursors.value[page + 1] = docs[docs.length - 1];
                hasMore.value = true;
              } else {
                hasMore.value = false;
              }

              resolve(null);
            },
            (error) => {
              console.error(error);
              reject(error);
            },
          );

          return;
        }

        unsubscribeCases();
        currentPage.value = page;

        const constraints: any[] = [
          ...searchQueries,
          ...orderBys,
          limit(pageSize.value + 5), // Fetch one extra to check if there are more pages
        ];

        // Add pagination cursor if not first page
        if (page > 0 && pageCursors.value[page]) {
          constraints.push(startAfter(pageCursors.value[page]));
        }

        const q = query(collection(db, "cases"), ...constraints).withConverter(
          caseConverter,
        );

        unsubscribeCases = onSnapshot(
          q,
          (querySnapshot) => {
            const docs = querySnapshot.docs.slice(0, pageSize.value); // Take only pageSize items

            caseDocs.value = docs;
            cases.value = docs.map((doc) => ({
              id: doc.id,
              ...doc.data(),
            })) as ICase[];

            // Save cursor for next page if we have results that is more than pageSize, meaning next page still has data
            if (
              querySnapshot.docs.length > pageSize.value &&
              docs[docs.length - 1]
            ) {
              pageCursors.value[page + 1] = docs[docs.length - 1];
              hasMore.value = true;
            } else {
              hasMore.value = false;
            }

            resolve(null);
          },
          (error) => {
            console.error(error);
            reject(error);
          },
        );
      } catch (e) {
        console.error(e);
        reject(e);
      }
    });
  };

  const fetchCase = async () => {
    return new Promise((resolve, reject) => {
      try {
        const userStore = useUserStore();
        const userPermissions = userStore.currentUser?.permissions || [];
        const currentUser = userStore.currentUser;

        // Check if user has permission to read cases
        const hasCaseReadOther = userPermissions.includes(
          PermissionType.CASE_READ_OTHER,
        );
        const hasCaseReadSelf = userPermissions.includes(
          PermissionType.CASE_READ_SELF,
        );

        if (!hasCaseReadOther && !hasCaseReadSelf) {
          selectedCase.value = undefined;
          resolve(null);
          return;
        }

        unsubscribeCase();

        if (!selectedCaseRegNum.value) {
          selectedCase.value = undefined;
          resolve(null);
          return;
        }

        // If user only has CASE_READ_SELF, check permissions after fetching
        if (!hasCaseReadOther && hasCaseReadSelf && currentUser) {
          const q = query(
            collection(db, "cases"),
            where("regNum", "==", selectedCaseRegNum.value),
            limit(1),
          ).withConverter(caseConverter);

          unsubscribeCase = onSnapshot(q, (querySnapshot) => {
            if (!querySnapshot.empty && querySnapshot.docs?.[0]?.exists()) {
              const caseData = querySnapshot.docs[0].data() as ICase;
              const hasAccess = checkUserCaseAccess(currentUser, caseData);

              if (hasAccess) {
                selectedCase.value = caseData;
              } else {
                selectedCase.value = undefined;
              }
            } else {
              selectedCase.value = undefined;
            }
            resolve(null);
          });
        } else {
          // User has CASE_READ_OTHER permission, can read any case
          const q = query(
            collection(db, "cases"),
            where("regNum", "==", selectedCaseRegNum.value),
            limit(1),
          ).withConverter(caseConverter);

          unsubscribeCase = onSnapshot(q, (querySnapshot) => {
            if (!querySnapshot.empty && querySnapshot.docs?.[0]?.exists()) {
              selectedCase.value = querySnapshot.docs[0].data() as ICase;
            }
            resolve(null);
          });
        }
      } catch (e) {
        console.error(e);
        reject(e);
      }
    });
  };

  const selectedCasePriceDoc = useDocument(
    computed(() => {
      if (!selectedCase.value) return null;

      return doc(db, `casesPricing/${selectedCase.value.id}`);
    }),
  );

  const selectedCasePrice = computed(() => {
    return selectedCasePriceDoc.value?.payment ?? null;
  });

  const selectedCaseLog = computed(() => {
    return null;
    // return selectedCaseLogDoc.value?.content ?? null
  });

  // Filtered cases based on user role
  const filteredCases = computed(() => {
    return cases.value.map((caseData) => filterCaseByRole(caseData));
  });

  // Filtered selected case based on user role
  const filteredSelectedCase = computed(() => {
    return selectedCase.value
      ? filterCaseByRole(selectedCase.value)
      : undefined;
  });

  // Watch user store changes and refetch data when currentUser exists
  watch(
    () => currentUser.value?.id,
    () => {
      if (currentUser.value?.id) {
        console.log("currentUser changed:", currentUser.value?.id);
        fetchCases();
        fetchCase();
      }
    },
    { immediate: true },
  );

  const nextPage = async () => {
    // Check if we have a cursor for the next page (meaning there are more results)
    if (pageCursors.value[currentPage.value + 1]) {
      return fetchCases(currentPage.value + 1);
    }
  };

  const prevPage = async () => {
    if (currentPage.value > 0) {
      return fetchCases(currentPage.value - 1);
    }
  };

  const setPageSize = async (newPageSize) => {
    caseDocs.value = [];
    pageCursors.value = [];
    currentPage.value = 0;
    pageSize.value = newPageSize;
    return fetchCases(0);
  };

  const resolvePhoneConditions = async (filters: CaseSearchFilter[]) => {
    const permissions = currentUser.value?.permissions || [];
    const maxValues = permissions.includes(PermissionType.CASE_READ_OTHER)
      ? PHONE_SEARCH_MAX_VALUES
      : PHONE_SEARCH_MAX_VALUES_WITH_OR;
    const { resolvePhoneSearchCondition } = usePhoneSearch();

    const entries = await Promise.all(
      filters
        .filter(
          (filter) =>
            filter.operator === "==" &&
            PHONE_SEARCH_FIELDS.includes(filter.field) &&
            filter.value,
        )
        .map(
          async (filter) =>
            [
              filter.field,
              await resolvePhoneSearchCondition(String(filter.value), {
                maxValues,
              }),
            ] as const,
        ),
    );
    return Object.fromEntries(entries);
  };

  const setSearchFilters = async (filters: typeof searchFilters.value) => {
    const requestId = ++phoneResolveRequestId;
    const phoneConditions = await resolvePhoneConditions(filters);
    // a newer search started while this one was resolving
    if (requestId !== phoneResolveRequestId) return;

    resolvedPhoneConditions.value = phoneConditions;
    searchFilters.value = filters;
    caseDocs.value = [];
    cases.value = [];
    pageCursors.value = [];
    currentPage.value = 0;
    return fetchCases(0);
  };

  /** NOTE: select case will clear selected case if regNum is not found */
  const selectCase = (regNum?: string) => {
    selectedCaseRegNum.value = regNum || "";
    return fetchCase();
  };

  // const selectedCase = useDocument<ICase>(computed(() => {
  //   if (!selectedCaseRegNum.value) return null

  //   return doc(db, `cases/${selectedCaseRegNum.value}`)
  // }))

  return {
    selectedCase: filteredSelectedCase,
    selectedCasePrice,
    selectedCaseLog,
    cases: filteredCases,
    currentPage,
    nextPage,
    prevPage,
    setPageSize,
    setSearchFilters,
    selectCase,
    generateCaseRegNumInfo,
    pageSize,
    hasMore,
  };
});

export default useCaseStore;
